import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

interface CreditTransaction {
  id: string;
  userId: string;
  type: 'purchase' | 'deduction' | 'bonus';
  amount: number;
  description: string;
  createdAt: Date;
  packageId?: string;
}

interface UseCreditsReturn {
  credits: number;
  isLoading: boolean;
  error: string | null;
  transactions: CreditTransaction[];
  purchaseCredits: (packageId: string, paymentMethod: string) => Promise<void>;
  deductCredit: (storyId: string) => Promise<boolean>;
  refreshCredits: () => Promise<void>;
  canGenerateStory: boolean;
}

export const useCredits = (): UseCreditsReturn => {
  const { user, updateUser } = useAuth();
  const [credits, setCredits] = useState(user?.credits || 0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);

  // Sync credits with user context
  useEffect(() => {
    if (user?.credits !== undefined) {
      setCredits(user.credits);
    }
  }, [user?.credits]);

  // Fetch current credits from backend
  const refreshCredits = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await api.get('/credits/balance');
      const newCredits = response.data.credits;
      
      setCredits(newCredits);
      updateUser({ ...user, credits: newCredits });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch credits');
      console.error('Error fetching credits:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, updateUser]);

  // Purchase credits
  const purchaseCredits = useCallback(async (
    packageId: string, 
    paymentMethod: string
  ): Promise<void> => {
    if (!user) throw new Error('User not authenticated');
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Call your payment API endpoint
      const response = await api.post('/credits/purchase', {
        packageId,
        paymentMethod,
        userId: user.id
      });

      // Handle successful purchase
      if (response.data.success) {
        const newCredits = response.data.credits;
        setCredits(newCredits);
        updateUser({ ...user, credits: newCredits });
        
        // Optionally fetch transaction history
        await fetchTransactions();
        
        // Show success notification (you might want to use a toast library)
        console.log('Credits purchased successfully!');
      } else {
        throw new Error(response.data.message || 'Purchase failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to purchase credits');
      console.error('Purchase error:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [user, updateUser]);

  // Deduct credit for story generation
  const deductCredit = useCallback(async (storyId: string): Promise<boolean> => {
    if (!user) return false;
    
    if (credits < 1) {
      setError('Insufficient credits');
      return false;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/credits/deduct', {
        userId: user.id,
        storyId,
        amount: 1
      });
      
      if (response.data.success) {
        const newCredits = credits - 1;
        setCredits(newCredits);
        updateUser({ ...user, credits: newCredits });
        return true;
      }
      
      return false;
    } catch (err: any) {
      setError(err.message || 'Failed to deduct credit');
      console.error('Deduction error:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, credits, updateUser]);

  // Fetch transaction history
  const fetchTransactions = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await api.get(`/credits/transactions/${user.id}`);
      setTransactions(response.data.transactions);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    }
  }, [user]);

  // Load transactions on mount
  useEffect(() => {
    if (user) {
      fetchTransactions();
    }
  }, [user, fetchTransactions]);

  return {
    credits,
    isLoading,
    error,
    transactions,
    purchaseCredits,
    deductCredit,
    refreshCredits,
    canGenerateStory: credits > 0
  };
};